import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { getStatusToken, TERMINAL_STATUSES } from '../utils/statusTokens';

const formatStamp = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return null;
  }
};

/**
 * Timeline: created -> sent -> viewed -> signed/declined/expired.
 * Each node is colored once reached; pending nodes are muted.
 */
const ApprovalTimeline = ({ approval }) => {
  if (!approval) return null;

  const terminalKey = TERMINAL_STATUSES.has(approval.status) ? approval.status : null;
  const terminalLabel = terminalKey ? getStatusToken(terminalKey).label : 'Outcome';
  const terminalStamp =
    formatStamp(approval.signed_at) ||
    formatStamp(approval.declined_at) ||
    formatStamp(approval.expires_at);

  const steps = [
    { key: 'created', label: 'Created', stamp: formatStamp(approval.created_at), reached: true },
    { key: 'sent', label: 'Sent', stamp: formatStamp(approval.sent_at), reached: !!approval.sent_at },
    { key: 'viewed', label: 'Viewed', stamp: formatStamp(approval.viewed_at), reached: !!approval.viewed_at },
    {
      key: 'terminal',
      label: terminalLabel,
      stamp: terminalKey ? terminalStamp : null,
      reached: !!terminalKey,
      tone: terminalKey ? getStatusToken(terminalKey) : null,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 2.5,
        py: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        Timeline
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={0}
        sx={{ mt: 1.5, alignItems: { sm: 'flex-start' } }}
      >
        {steps.map((step, i) => {
          const tone = step.tone;
          const dotColor = step.reached ? (tone?.rail || '#1F4ED8') : '#CBD5E1';
          const connectorColor = step.reached ? (tone?.rail || '#1F4ED8') : '#E2E8F0';
          return (
            <Box key={step.key} sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 1.5, mt: '2px' }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: dotColor,
                    boxShadow: step.reached ? `0 0 0 3px ${dotColor}22` : 'none',
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, pb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: step.reached ? 'text.primary' : 'text.disabled' }}
                >
                  {step.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {step.stamp || (step.reached ? '—' : 'Pending')}
                </Typography>
              </Box>
              {i < steps.length - 1 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: 2, sm: 32 },
                    height: { xs: 16, sm: 2 },
                    bgcolor: connectorColor,
                    mt: { xs: 0, sm: '6px' },
                    mr: { xs: 0, sm: 1.5 },
                    ml: { xs: '4px', sm: 0 },
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ApprovalTimeline;
